import React, { useState, useMemo, useEffect } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  FileText,
  Calendar,
  X,
  Loader,
  Clock,
  XCircle,
  Download
} from 'lucide-react';
import { formatCurrency, formatDate, subAgreementRequestsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SubAgreementManagement = () => {
  const { user: authUser } = useAuth() || {};
  const [subAgreementBonds, setSubAgreementBonds] = useState([]);
  const [allBondsForFilter, setAllBondsForFilter] = useState([]); // All bonds for filtering (not paginated)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'Pending', 'L1 Approved', 'Approved', 'Rejected'
  const [loadingAllBonds, setLoadingAllBonds] = useState(false);

  // Sub Agreement Management states
  const [subAgreementData, setSubAgreementData] = useState({});
  const [subAgreementLoading, setSubAgreementLoading] = useState({});
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editingAmount, setEditingAmount] = useState('');
  const [originalEditAmount, setOriginalEditAmount] = useState('');
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [approvingBonds, setApprovingBonds] = useState(new Set()); // Set of bondIds currently being approved
  const [approvingRequests, setApprovingRequests] = useState(new Set()); // Set of requestIds currently being approved in modal
  const [rejectingBonds, setRejectingBonds] = useState(new Set()); // Set of bondIds currently being rejected
  const [rejectingRequests, setRejectingRequests] = useState(new Set()); // Set of requestIds currently being rejected
  const [subAgreementApprovalStates, setSubAgreementApprovalStates] = useState({}); // requestId -> approval status
  const [bondSubAgreementSummary, setBondSubAgreementSummary] = useState({}); // bondId -> { primarySubscribers: [] }

  // Details modal state
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    request: null,
    loading: false
  });
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [pdfBlobUrls, setPdfBlobUrls] = useState({}); // Store blob URLs by submission ID

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    onConfirm: null,
    showInput: false,
    inputLabel: '',
    inputPlaceholder: '',
    inputValue: '',
    isTextArea: false
  });

  // Unified notification state
  const [notice, setNotice] = useState({ open: false, type: 'info', title: '', message: '' });
  const showNotice = (type, title, message) => setNotice({ open: true, type, title, message });
  const closeNotice = () => setNotice(n => ({ ...n, open: false }));

  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (notice.open) {
      const timer = setTimeout(() => {
        closeNotice();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notice.open]);

  const fetchSubAgreementBonds = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      setError(null);

      const response = await subAgreementRequestsAPI.getBondsWithRequestsForFA(page, limit);

      if (response.data.success) {
        const data = response.data.data;
        const bonds = data?.bonds || [];
        const pagination = data?.pagination || {};

        setSubAgreementBonds(bonds);
        setCurrentPage(pagination.page || 1);
        setTotalPages(pagination.totalPages || 1);
        setTotalRecords(pagination.total || 0);
      } else {
        throw new Error(response.data.message || 'Failed to fetch bonds');
      }
    } catch (error) {
      setError(error.message);
      setSubAgreementBonds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubAgreementBonds();
  }, []);

  // Fetch all bonds when filter is applied (not 'all')
  useEffect(() => {
    const fetchAllBondsForFilter = async () => {
      if (statusFilter === 'all') {
        // Reset to paginated view when filter is 'all'
        setAllBondsForFilter([]);
        return;
      }

      try {
        setLoadingAllBonds(true);
        // Fetch all bonds with a large limit
        const response = await subAgreementRequestsAPI.getBondsWithRequestsForFA(1, 10000);
        if (response.data.success) {
          const bonds = response.data.data?.bonds || [];
          setAllBondsForFilter(bonds);

          // Also fetch all the Sub Agreement data for these bonds
          const allBondData = {};
          const allApprovalStates = {};

          await Promise.all(
            bonds.map(async (bond) => {
              try {
                const bondResponse = await subAgreementRequestsAPI.getRequestsByBondForFA(bond.id);
                if (bondResponse.data.success) {
                  const requests = bondResponse.data.data?.requests || bondResponse.data.data || [];

                  const primarySubscribers = requests.map(req => ({
                    id: req._id || req.id,
                    requestId: req._id || req.id,
                    name: req.primarySubscriberId?.corporateInfo?.companyName ||
                      req.primarySubscriberId?.corporateInfo?.legalEntityName ||
                      [req.primarySubscriberId?.personalInfo?.firstName, req.primarySubscriberId?.personalInfo?.lastName].filter(Boolean).join(' ') ||
                      req.primarySubscriberId?.email ||
                      'Unknown',
                    email: req.primarySubscriberId?.email || '—',
                    totalRequestedAmount: req.totalRequestedAmount || 0,
                    totalAmount: req.totalRequestedAmount || 0,
                    submissions: (req.submissions || []).map(sub => ({
                      _id: sub._id,
                      id: sub._id,
                      amount: sub.amount,
                      notes: sub.notes,
                      submittedAt: sub.submittedAt,
                      uploadedAt: sub.submittedAt,
                      fileName: sub.subAgreementFileId?.originalName || sub.subAgreementFileId?.filename || 'Sub Agreement Document',
                      fileSize: sub.subAgreementFileId?.size,
                      fileUrl: sub.subAgreementFileId?.pbRecordId
                        ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
                        : null,
                      url: sub.subAgreementFileId?.pbRecordId
                        ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
                        : null
                    })),
                    __type: 'primarySubscriber'
                  }));

                  allBondData[bond.id] = { primarySubscribers };

                  // Store approval states
                  requests.forEach(req => {
                    const reqId = req._id || req.id;
                    const l1Approver = req.l1Approver || req.l1ApproverId;
                    const l2Approver = req.l2Approver || req.l2ApproverId;
                    const rejectedByUser = req.rejectedByUser || req.rejectedBy;

                    allApprovalStates[reqId] = {
                      status: req.status || 'submitted',
                      l1ApprovedBy: l1Approver || null,
                      l1ApprovedAt: req.l1ApprovedAt || null,
                      l2ApprovedBy: l2Approver || null,
                      l2ApprovedAt: req.l2ApprovedAt || null,
                      rejectedBy: rejectedByUser || null,
                      rejectedAt: req.rejectedAt || null
                    };
                  });
                }
              } catch (error) {
                console.error(`Error fetching data for bond ${bond.id}:`, error);
              }
            })
          );

          // Update the data states with all bonds data
          setSubAgreementData(prev => ({ ...prev, ...allBondData }));
          setSubAgreementApprovalStates(prev => ({ ...prev, ...allApprovalStates }));
          setBondSubAgreementSummary(prev => ({ ...prev, ...allBondData }));
        }
      } catch (error) {
        console.error('Error fetching all bonds for filter:', error);
        showNotice('error', 'Filter Error', 'Failed to load all bonds for filtering');
      } finally {
        setLoadingAllBonds(false);
      }
    };

    fetchAllBondsForFilter();
  }, [statusFilter]);

  // Load Sub Agreement data for all bonds
  useEffect(() => {
    const loadSubAgreementData = async () => {
      if (subAgreementBonds.length === 0) return;

      const summaryData = {};
      const approvalStates = {};
      const detailedData = {};

      // Batch load all bond data concurrently instead of sequentially
      await Promise.all(
        subAgreementBonds.map(async (bond) => {
          try {
            setSubAgreementLoading(prev => ({ ...prev, [bond.id]: true }));
            const response = await subAgreementRequestsAPI.getRequestsByBondForFA(bond.id);
            if (response.data.success) {
              const requests = response.data.data?.requests || response.data.data || [];
              console.log('Raw requests data for bond', bond.id, ':', requests);

              const primarySubscribers = requests.map(req => ({
                id: req._id || req.id,
                requestId: req._id || req.id,
                name: req.primarySubscriberId?.corporateInfo?.companyName ||
                  req.primarySubscriberId?.corporateInfo?.legalEntityName ||
                  [req.primarySubscriberId?.personalInfo?.firstName, req.primarySubscriberId?.personalInfo?.lastName].filter(Boolean).join(' ') ||
                  req.primarySubscriberId?.email ||
                  'Unknown',
                email: req.primarySubscriberId?.email || '—',
                totalRequestedAmount: req.totalRequestedAmount || 0,
                totalAmount: req.totalRequestedAmount || 0,
                submissions: (req.submissions || []).map(sub => {
                  console.log('Processing submission:', sub);
                  console.log('subAgreementFileId:', sub.subAgreementFileId);
                  const fileUrl = sub.subAgreementFileId?.pbRecordId
                    ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
                    : null;
                  console.log('Constructed fileUrl:', fileUrl);

                  return {
                    _id: sub._id,
                    id: sub._id,
                    amount: sub.amount,
                    notes: sub.notes,
                    submittedAt: sub.submittedAt,
                    uploadedAt: sub.submittedAt,
                    fileName: sub.subAgreementFileId?.originalName || sub.subAgreementFileId?.filename || 'Sub Agreement Document',
                    fileSize: sub.subAgreementFileId?.size,
                    fileUrl: fileUrl,
                    url: fileUrl
                  };
                }),
                __type: 'primarySubscriber'
              }));

              console.log('Processed primarySubscribers:', primarySubscribers);

              summaryData[bond.id] = { primarySubscribers };
              detailedData[bond.id] = { primarySubscribers };

              // Store approval states for each request from actual backend data
              requests.forEach(req => {
                const reqId = req._id || req.id;
                // Try to get approver info from populated fields or IDs
                const l1Approver = req.l1Approver || req.l1ApproverId;
                const l2Approver = req.l2Approver || req.l2ApproverId;
                const rejectedByUser = req.rejectedByUser || req.rejectedBy;

                approvalStates[reqId] = {
                  status: req.status || 'submitted',
                  l1ApprovedBy: l1Approver || null,
                  l1ApprovedAt: req.l1ApprovedAt || null,
                  l2ApprovedBy: l2Approver || null,
                  l2ApprovedAt: req.l2ApprovedAt || null,
                  rejectedBy: rejectedByUser || null,
                  rejectedAt: req.rejectedAt || null,
                  cashTokenMinted: req.cashTokenMinted || false,
                  cashTokenMintHash: req.cashTokenMintHash || null
                };
              });
            }
          } catch (error) {
            // Error loading bond data
          } finally {
            setSubAgreementLoading(prev => ({ ...prev, [bond.id]: false }));
          }
        })
      );

      setBondSubAgreementSummary(summaryData);
      setSubAgreementApprovalStates(approvalStates);
      setSubAgreementData(detailedData);
    };

    loadSubAgreementData();
  }, [subAgreementBonds]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Review': { color: 'yellow', icon: Clock },
      'Ready for Allocation': { color: 'warning', icon: AlertTriangle },
      'Allocation Uploaded': { color: 'success', icon: CheckCircle },
      'Pending L1': { color: 'blue', icon: Clock },
      'Pending L2': { color: 'indigo', icon: Clock },
      'Approved': { color: 'green', icon: CheckCircle },
    };
    const config = statusConfig[status] || { color: 'secondary', icon: FileText };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const refreshSubAgreementData = async (bondId) => {
    if (!bondId) return;
    try {
      const response = await subAgreementRequestsAPI.getRequestsByBondForFA(bondId);
      if (response.data.success) {
        const requests = response.data.data?.requests || response.data.data || [];

        const primarySubscribers = requests.map(req => ({
          id: req._id || req.id,
          requestId: req._id || req.id,
          name: req.primarySubscriberId?.corporateInfo?.companyName ||
            req.primarySubscriberId?.corporateInfo?.legalEntityName ||
            [req.primarySubscriberId?.personalInfo?.firstName, req.primarySubscriberId?.personalInfo?.lastName].filter(Boolean).join(' ') ||
            req.primarySubscriberId?.email ||
            'Unknown',
          email: req.primarySubscriberId?.email || '—',
          totalRequestedAmount: req.totalRequestedAmount || 0,
          totalAmount: req.totalRequestedAmount || 0,
          submissions: (req.submissions || []).map(sub => {
            const fileUrl = sub.subAgreementFileId?.pbRecordId
              ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
              : null;

            return {
              _id: sub._id,
              id: sub._id,
              amount: sub.amount,
              notes: sub.notes,
              submittedAt: sub.submittedAt,
              uploadedAt: sub.submittedAt,
              fileName: sub.subAgreementFileId?.originalName || sub.subAgreementFileId?.filename || 'Sub Agreement Document',
              fileSize: sub.subAgreementFileId?.size,
              fileUrl: fileUrl,
              url: fileUrl
            };
          }),
          __type: 'primarySubscriber'
        }));

        setSubAgreementData(prev => ({
          ...prev,
          [bondId]: { primarySubscribers }
        }));

        // Update bond summary as well
        setBondSubAgreementSummary(prev => ({
          ...prev,
          [bondId]: { primarySubscribers }
        }));

        // Update approval states from backend data
        const approvalStates = {};
        requests.forEach(req => {
          const reqId = req._id || req.id;
          // Try to get approver info from populated fields or IDs
          const l1Approver = req.l1Approver || req.l1ApproverId;
          const l2Approver = req.l2Approver || req.l2ApproverId;
          const rejectedByUser = req.rejectedByUser || req.rejectedBy;

          approvalStates[reqId] = {
            status: req.status || 'submitted',
            l1ApprovedBy: l1Approver || null,
            l1ApprovedAt: req.l1ApprovedAt || null,
            l2ApprovedBy: l2Approver || null,
            l2ApprovedAt: req.l2ApprovedAt || null,
            rejectedBy: rejectedByUser || null,
            rejectedAt: req.rejectedAt || null,
            cashTokenMinted: req.cashTokenMinted || false,
            cashTokenMintHash: req.cashTokenMintHash || null
          };
        });
        setSubAgreementApprovalStates(prev => ({ ...prev, ...approvalStates }));
      }
    } catch (error) {
      showNotice('error', 'Error', 'Failed to refresh Sub Agreement data');
    }
  };



  const getSubmissionIdentifiers = (submission = {}) => {
    const submissionId = submission._id || submission.id || submission.subAgreementFileId;
    const requestId = submission.requestId;
    return { submissionId, requestId };
  };

  const handleStartEditSubmission = (primarySubscriber) => {
    const requestId = primarySubscriber.id || primarySubscriber.requestId;
    setEditingRequestId(requestId);
    const amountStr = typeof primarySubscriber.totalRequestedAmount === 'number'
      ? primarySubscriber.totalRequestedAmount.toString()
      : (typeof primarySubscriber.totalAmount === 'number' ? primarySubscriber.totalAmount.toString() : '');
    setEditingAmount(amountStr);
    // Store the current amount for comparison to detect if changed
    setOriginalEditAmount(amountStr);
  };

  const handleCancelEditSubmission = () => {
    setEditingRequestId(null);
    setEditingAmount('');
    setOriginalEditAmount('');
  };

  const handleSaveSubmissionAmount = async () => {
    if (!editingRequestId || !editingAmount) {
      showNotice('error', 'Error', 'Missing required information');
      return;
    }

    const amountValue = Number(editingAmount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      showNotice('error', 'Error', 'Please enter a valid amount');
      return;
    }

    // If amount hasn't changed, just cancel the edit
    if (editingAmount === originalEditAmount) {
      handleCancelEditSubmission();
      return;
    }

    // Prevent adjusted amount from exceeding original amount
    const approval = subAgreementApprovalStates[editingRequestId];
    if (!approval) {
      showNotice('error', 'Invalid Request', 'Could not find approval data.');
      return;
    }

    // Use originalRequestedAmount from backend, fallback to totalRequestedAmount if not set yet
    const originalAmount = approval.originalRequestedAmount || approval.totalRequestedAmount;
    if (amountValue > originalAmount) {
      showNotice('error', 'Invalid Amount', `Adjusted amount cannot exceed original amount of ${formatCurrency(originalAmount)}`);
      return;
    }

    try {
      setSubmissionSaving(true);
      const response = await subAgreementRequestsAPI.updateTotalRequestedAmount(
        editingRequestId,
        { amount: amountValue }
      );

      if (response.data.success) {
        showNotice('success', 'Amount Updated', 'Total requested amount has been updated successfully');
        handleCancelEditSubmission();
        // Find the bond ID from the current editing context
        const bondId = Object.keys(subAgreementData).find(id => {
          const bond = subAgreementData[id];
          return bond?.primarySubscribers?.some(ps => ps.id === editingRequestId);
        });
        if (bondId) {
          await refreshSubAgreementData(bondId);
        }
      } else {
        showNotice('error', 'Update Failed', response.data.message || 'Failed to update amount');
      }
    } catch (error) {
      showNotice('error', 'Update Failed', error.response?.data?.message || 'Failed to update amount');
    } finally {
      setSubmissionSaving(false);
    }
  };

  const handleRejectSubAgreement = async (requestId) => {
    if (!requestId) {
      showNotice('error', 'Error', 'Invalid request ID');
      return;
    }

    // Determine rejection level based on current approval state
    const approval = subAgreementApprovalStates[requestId];
    const level = approval?.l1ApprovedBy ? 'L2' : 'L1';

    setConfirmModal({
      open: true,
      title: 'Reject Sub Agreement',
      message: 'Are you sure you want to reject this Sub Agreement request? This action cannot be undone.',
      confirmLabel: 'Reject',
      showInput: true,
      inputLabel: 'Rejection reason (required)',
      inputPlaceholder: 'Enter reason for rejection...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (reason) => {
        if (!reason || reason.trim() === '') {
          showNotice('error', 'Error', 'Rejection reason is required');
          return;
        }

        try {
          setRejectingRequests(prev => new Set([...prev, requestId]));

          const response = await subAgreementRequestsAPI.rejectRequest(requestId, { level, reason });

          if (response.data.success) {
            const request = response.data.data?.request || response.data.data;

            // Update approval state with rejection data
            setSubAgreementApprovalStates(prev => ({
              ...prev,
              [requestId]: {
                ...prev[requestId],
                status: 'rejected',
                rejectedBy: request.rejectedBy,
                rejectedAt: request.rejectedAt,
                rejectionReason: request.rejectionReason
              }
            }));

            showNotice('success', 'Rejection Successful', 'Sub Agreement request has been rejected');

            // Refresh the entire bond list to remove bonds with no remaining requests
            await fetchSubAgreementBonds();
          } else {
            throw new Error(response.data.message || 'Rejection failed');
          }
        } catch (error) {
          showNotice('error', 'Rejection Failed', error.response?.data?.message || 'Failed to reject request');
        } finally {
          setRejectingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
          });
        }
      }
    });
  };

  const handleApproveSubAgreement = async (requestId, level) => {
    if (!requestId) {
      showNotice('error', 'Error', 'Invalid request ID');
      return;
    }

    const confirmMessage = level === 'L1'
      ? 'Are you sure you want to approve this Sub Agreement request at Level 1?'
      : 'Are you sure you want to approve this Sub Agreement request at Level 2? This will mint cash tokens to the Primary Subscriber.';

    setConfirmModal({
      open: true,
      title: `${level} Approval`,
      message: confirmMessage,
      confirmLabel: `Approve ${level}`,
      showInput: true,
      inputLabel: `Optional notes for ${level} approval`,
      inputPlaceholder: 'Enter notes (optional)...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (notes) => {
        try {
          setApprovingRequests(prev => new Set([...prev, requestId]));

          const approvalData = { notes: notes || '' };
          const response = level === 'L1'
            ? await subAgreementRequestsAPI.approveL1(requestId, approvalData)
            : await subAgreementRequestsAPI.approveL2(requestId, approvalData);

          if (response.data.success) {
            const request = response.data.data?.request || response.data.data;

            // Update approval state with actual data
            setSubAgreementApprovalStates(prev => ({
              ...prev,
              [requestId]: {
                l1ApprovedBy: request.l1ApproverId,
                l1ApprovedAt: request.l1ApprovedAt,
                l2ApprovedBy: request.l2ApproverId,
                l2ApprovedAt: request.l2ApprovedAt,
                status: request.status,
                cashTokenMinted: request.cashTokenMinted
              }
            }));

            showNotice(
              'success',
              'Approval Successful',
              level === 'L2'
                ? `Sub Agreement request approved at ${level} and cash tokens minted successfully`
                : `Sub Agreement request approved at ${level}`
            );

            // Find and refresh the bond data
            const bondId = Object.keys(subAgreementData).find(id => {
              const bond = subAgreementData[id];
              return bond?.primarySubscribers?.some(ps => ps.id === requestId);
            });
            if (bondId) {
              await refreshSubAgreementData(bondId);
            }
          } else {
            throw new Error(response.data.message || 'Approval failed');
          }
        } catch (error) {
          showNotice('error', 'Approval Failed', error.response?.data?.message || `Failed to approve at ${level}`);
        } finally {
          setApprovingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
          });
        }
      }
    });
  };

  const handleBondRejection = async (bond) => {
    const subAgreementData = bondSubAgreementSummary[bond.id];
    if (!subAgreementData) {
      showNotice('error', 'Error', 'No Sub Agreement data available for this bond');
      return;
    }

    const allRequestIds = (subAgreementData.primarySubscribers || []).map(ps => ps.id).filter(Boolean);

    if (allRequestIds.length === 0) {
      showNotice('error', 'Error', 'No Sub Agreement requests to reject');
      return;
    }

    // Filter out already-rejected requests
    const pendingRequestIds = allRequestIds.filter(requestId => {
      const approval = subAgreementApprovalStates[requestId];
      return !approval || approval.status !== 'rejected';
    });

    if (pendingRequestIds.length === 0) {
      showNotice('info', 'No Action Needed', 'All Sub Agreement requests are already rejected');
      return;
    }

    const skippedCount = allRequestIds.length - pendingRequestIds.length;
    const skippedMessage = skippedCount > 0 ? ` (${skippedCount} already rejected will be skipped)` : '';

    // Determine rejection level - use L1 for bulk rejection by default
    // Individual requests that have L1 approval will be rejected at L2 level by the backend
    const level = 'L1';

    setConfirmModal({
      open: true,
      title: 'Bulk Rejection',
      message: `Reject ${pendingRequestIds.length} Sub Agreement requests for "${bond.bondName}"?${skippedMessage}`,
      confirmLabel: `Reject ${pendingRequestIds.length} Requests`,
      showInput: true,
      inputLabel: 'Rejection reason (required)',
      inputPlaceholder: 'Enter reason for rejection...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (reason) => {
        if (!reason || reason.trim() === '') {
          showNotice('error', 'Error', 'Rejection reason is required');
          return;
        }

        try {
          setRejectingBonds(prev => new Set([...prev, bond.id]));

          const response = await subAgreementRequestsAPI.bulkReject(bond.id, { level, reason });

          if (response.data.success) {
            const result = response.data.data;
            const successCount = result.results?.filter(r => r.success).length || result.total || 0;
            const failureCount = result.results?.filter(r => !r.success).length || 0;

            if (failureCount > 0) {
              showNotice(
                'warning',
                'Partial Success',
                `${successCount} requests rejected, ${failureCount} failed`
              );
            } else {
              showNotice(
                'success',
                'Rejection Successful',
                `${successCount} Sub Agreement requests have been rejected`
              );
            }

            // Refresh the bond list to remove bonds with no remaining requests
            await fetchSubAgreementBonds();
          } else {
            throw new Error(response.data.message || 'Bulk rejection failed');
          }
        } catch (error) {
          showNotice('error', 'Rejection Failed', error.response?.data?.message || 'Failed to reject requests');
        } finally {
          setRejectingBonds(prev => {
            const newSet = new Set(prev);
            newSet.delete(bond.id);
            return newSet;
          });
        }
      }
    });
  };

  const handleViewDetails = async (primarySubscriber) => {
    setDetailsModal({
      open: true,
      request: primarySubscriber,
      loading: false
    });
  };

  const handleCloseDetailsModal = () => {
    // Clean up blob URLs
    Object.values(pdfBlobUrls).forEach(url => {
      if (url) window.URL.revokeObjectURL(url);
    });
    setPdfBlobUrls({});

    setDetailsModal({
      open: false,
      request: null,
      loading: false
    });
    setSelectedPdfUrl(null);
  };

  // Helper function to get the status string for a primary subscriber
  const getPrimarySubscriberStatus = (requestId) => {
    const approval = subAgreementApprovalStates[requestId];
    if (!approval) return 'Pending';

    // Use status as primary source of truth for rejection
    if (approval.status === 'rejected') return 'Rejected';

    // Check if fully approved (L2 approved)
    if (approval.l2ApprovedBy || approval.status === 'completed' || approval.status === 'l2_approved') {
      return 'Approved';
    }

    // Check if L1 approved
    if (approval.l1ApprovedBy) {
      return 'L1 Approved';
    }

    return 'Pending';
  };

  const handleDownloadPdf = async (submission) => {
    try {
      const fileUrl = submission.fileUrl || submission.url;
      if (!fileUrl) {
        showNotice('error', 'Error', 'No file URL available for download');
        return;
      }

      // Fetch the file with credentials to handle authentication
      const response = await fetch(fileUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = submission.fileName || `sub-agreement-${submission._id || submission.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

      showNotice('success', 'Download Started', 'PDF download has been initiated');
    } catch (error) {
      showNotice('error', 'Download Failed', 'Failed to download the PDF file');
    }
  };

  const handleBondApproval = async (bond, level) => {
    const subAgreementData = bondSubAgreementSummary[bond.id];
    if (!subAgreementData) {
      showNotice('error', 'Error', 'No Sub Agreement data available for this bond');
      return;
    }

    const allRequestIds = (subAgreementData.primarySubscribers || []).map(ps => ps.id).filter(Boolean);

    if (allRequestIds.length === 0) {
      showNotice('error', 'Error', 'No Sub Agreement requests to approve');
      return;
    }

    // Filter out already-approved requests
    const pendingRequestIds = allRequestIds.filter(requestId => {
      const approval = subAgreementApprovalStates[requestId];
      if (level === 'L1') {
        return !approval || !approval.l1ApprovedBy;
      } else {
        // For L2, check if L1 is approved and L2 is not
        const isL1Approved = approval && approval.l1ApprovedBy;
        const isL2Approved = approval && approval.l2ApprovedBy;
        // Also check same user restriction
        const l1ApproverId = approval?.l1ApprovedBy;
        const currentUserId = authUser?._id || authUser?.id;
        const isSameUser = l1ApproverId && currentUserId && String(l1ApproverId) === String(currentUserId);
        return isL1Approved && !isL2Approved && !isSameUser;
      }
    });

    if (pendingRequestIds.length === 0) {
      const message = level === 'L1'
        ? 'All Sub Agreement requests are already approved at Level 1'
        : 'All Sub Agreement requests are already approved at Level 2 or require a different user for L2 approval';
      showNotice('info', 'No Action Needed', message);
      return;
    }

    const skippedCount = allRequestIds.length - pendingRequestIds.length;
    const skippedMessage = skippedCount > 0 ? ` (${skippedCount} already approved will be skipped)` : '';

    const confirmMessage = level === 'L1'
      ? `Approve ${pendingRequestIds.length} Sub Agreement requests for "${bond.bondName}" at Level 1?${skippedMessage}`
      : `Approve ${pendingRequestIds.length} Sub Agreement requests for "${bond.bondName}" at Level 2? This will mint cash tokens to all Primary Subscribers.${skippedMessage}`;

    setConfirmModal({
      open: true,
      title: `Bond ${level} Approval`,
      message: confirmMessage,
      confirmLabel: `Approve ${pendingRequestIds.length} Requests`,
      showInput: true,
      inputLabel: `Optional notes for bulk ${level} approval`,
      inputPlaceholder: 'Enter notes (optional)...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (notes) => {
        try {
          setApprovingBonds(prev => new Set([...prev, bond.id]));

          const approvalData = { notes: notes || '' };
          const response = level === 'L1'
            ? await subAgreementRequestsAPI.bulkApproveL1(bond.id, approvalData)
            : await subAgreementRequestsAPI.bulkApproveL2(bond.id, approvalData);

          if (response.data.success) {
            const result = response.data.data;
            const successCount = result.results?.filter(r => r.success).length || result.total || 0;
            const failureCount = result.results?.filter(r => !r.success).length || 0;

            if (failureCount > 0) {
              showNotice(
                'warning',
                'Partial Success',
                `${successCount} requests approved at ${level}, ${failureCount} failed`
              );
            } else {
              showNotice(
                'success',
                'Approval Successful',
                level === 'L2'
                  ? `${successCount} Sub Agreement requests approved at ${level} and cash tokens minted successfully`
                  : `${successCount} Sub Agreement requests approved at ${level}`
              );
            }

            // Refresh data
            await fetchSubAgreementBonds();
          } else {
            throw new Error(response.data.message || 'Bulk approval failed');
          }
        } catch (error) {
          showNotice('error', 'Approval Failed', error.response?.data?.message || `Failed to approve at ${level}`);
        } finally {
          setApprovingBonds(prev => {
            const newSet = new Set(prev);
            newSet.delete(bond.id);
            return newSet;
          });
        }
      }
    });
  };

  const handleExportReport = async () => {
    try {
      showNotice('info', 'Exporting', 'Fetching all Sub Agreement data for export...');

      // Fetch ALL bonds (not paginated) for export
      const allBondsResponse = await subAgreementRequestsAPI.getBondsWithRequestsForFA(1, 10000); // Large limit to get all
      if (!allBondsResponse.data.success) {
        throw new Error('Failed to fetch bonds for export');
      }

      const allBonds = allBondsResponse.data.data?.bonds || [];

      // Fetch all Sub Agreement data for all bonds
      const allBondData = {};
      const allApprovalStates = {};
      const approverIds = new Set(); // Collect all unique approver IDs

      await Promise.all(
        allBonds.map(async (bond) => {
          try {
            const response = await subAgreementRequestsAPI.getRequestsByBondForFA(bond.id);
            if (response.data.success) {
              const requests = response.data.data?.requests || response.data.data || [];

              const primarySubscribers = requests.map(req => ({
                id: req._id || req.id,
                requestId: req._id || req.id,
                name: req.primarySubscriberId?.corporateInfo?.companyName ||
                  req.primarySubscriberId?.corporateInfo?.legalEntityName ||
                  [req.primarySubscriberId?.personalInfo?.firstName, req.primarySubscriberId?.personalInfo?.lastName].filter(Boolean).join(' ') ||
                  req.primarySubscriberId?.email ||
                  'Unknown',
                email: req.primarySubscriberId?.email || '—',
                totalRequestedAmount: req.totalRequestedAmount || 0,
                totalAmount: req.totalRequestedAmount || 0,
                submissions: (req.submissions || []).map(sub => ({
                  _id: sub._id,
                  id: sub._id,
                  amount: sub.amount,
                  notes: sub.notes,
                  submittedAt: sub.submittedAt,
                  uploadedAt: sub.submittedAt,
                  fileName: sub.subAgreementFileId?.originalName || sub.subAgreementFileId?.filename || 'Sub Agreement Document',
                  fileSize: sub.subAgreementFileId?.size,
                  fileUrl: sub.subAgreementFileId?.pbRecordId
                    ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
                    : null,
                  url: sub.subAgreementFileId?.pbRecordId
                    ? `/api/files/pbrecord/${sub.subAgreementFileId.pbRecordId}/content`
                    : null
                })),
                __type: 'primarySubscriber'
              }));

              allBondData[bond.id] = { primarySubscribers };

              // Store approval states with approver info and collect approver IDs
              requests.forEach(req => {
                const reqId = req._id || req.id;
                const l1Approver = req.l1Approver || req.l1ApproverId;
                const l2Approver = req.l2Approver || req.l2ApproverId;
                const rejectedByUser = req.rejectedByUser || req.rejectedBy;

                // Collect approver IDs (if they're strings/IDs, not objects)
                if (l1Approver && typeof l1Approver === 'string') {
                  approverIds.add(l1Approver);
                } else if (l1Approver && typeof l1Approver === 'object' && l1Approver._id) {
                  approverIds.add(l1Approver._id);
                }

                if (l2Approver && typeof l2Approver === 'string') {
                  approverIds.add(l2Approver);
                } else if (l2Approver && typeof l2Approver === 'object' && l2Approver._id) {
                  approverIds.add(l2Approver._id);
                }

                if (rejectedByUser && typeof rejectedByUser === 'string') {
                  approverIds.add(rejectedByUser);
                } else if (rejectedByUser && typeof rejectedByUser === 'object' && rejectedByUser._id) {
                  approverIds.add(rejectedByUser._id);
                }

                allApprovalStates[reqId] = {
                  status: req.status || 'submitted',
                  l1ApprovedBy: l1Approver || null,
                  l1ApprovedAt: req.l1ApprovedAt || null,
                  l2ApprovedBy: l2Approver || null,
                  l2ApprovedAt: req.l2ApprovedAt || null,
                  rejectedBy: rejectedByUser || null,
                  rejectedAt: req.rejectedAt || null
                };
              });
            }
          } catch (error) {
            console.error(`Error fetching data for bond ${bond.id}:`, error);
          }
        })
      );

      // Fetch user emails for all approver IDs
      const approverEmailMap = new Map(); // Map of user ID to email
      if (approverIds.size > 0) {
        showNotice('info', 'Fetching Approver Details', `Fetching email addresses for ${approverIds.size} approvers...`);

        // Fetch user details for all approver IDs in parallel (with concurrency limit)
        const approverIdArray = Array.from(approverIds);
        const batchSize = 10; // Process 10 at a time to avoid overwhelming the server
        for (let i = 0; i < approverIdArray.length; i += batchSize) {
          const batch = approverIdArray.slice(i, i + batchSize);
          await Promise.all(
            batch.map(async (userId) => {
              try {
                const userResponse = await usersAPI.getUser(userId);
                if (userResponse.data.success && userResponse.data.data) {
                  const user = userResponse.data.data.user || userResponse.data.data;
                  approverEmailMap.set(userId, user.email || 'N/A');
                }
              } catch (error) {
                console.error(`Error fetching user ${userId}:`, error);
                approverEmailMap.set(userId, 'N/A');
              }
            })
          );
        }
      }

      // CSV Headers
      const headers = [
        'Bond Name',
        'ISIN',
        'Stock Code',
        'Product Type',
        'Issuer',
        'Primary Subscriber Name',
        'Primary Subscriber Email',
        'Total Requested Amount',
        'Approval Status',
        'L1 Approved By',
        'L1 Approved At',
        'L2 Approved By',
        'L2 Approved At',
        'Rejected By',
        'Rejected At',
        'Request Date'
      ];

      const rows = [headers];

      // Helper function to get approval status
      const getApprovalStatus = (approval) => {
        if (!approval) return 'Pending';
        if (approval.status === 'rejected') return 'Rejected';
        if (approval.l2ApprovedBy) return 'L2 Approved';
        if (approval.l1ApprovedBy) return 'L1 Approved';
        return 'Pending';
      };

      // Helper function to get approver email (prioritize email)
      const getApproverEmail = (approver) => {
        if (!approver) return 'N/A';
        if (typeof approver === 'object') {
          // If it's a populated user object, return email
          if (approver.email) {
            return approver.email;
          }
          // If it has _id, try to get email from map
          if (approver._id) {
            return approverEmailMap.get(approver._id) || 'N/A';
          }
          return 'N/A';
        }
        // If it's just an ID string, look it up in the map
        if (typeof approver === 'string') {
          return approverEmailMap.get(approver) || 'N/A';
        }
        return 'N/A';
      };

      // Helper function to format date
      const formatDateValue = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
          return formatDate(dateValue);
        } catch {
          return String(dateValue);
        }
      };

      // Iterate through all bonds
      allBonds.forEach((bond) => {
        const bondData = allBondData[bond.id];

        if (!bondData || !bondData.primarySubscribers || bondData.primarySubscribers.length === 0) {
          // If no Sub Agreement data, still add bond info with empty subscriber data
          rows.push([
            bond.bondName || bond.name || 'N/A',
            bond.isin || '-',
            bond.stockCode || '-',
            bond.productType || bond.bondType || 'N/A',
            bond.issuer || 'N/A',
            'N/A',
            'N/A',
            '0',
            'No Requests',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A'
          ]);
          return;
        }

        // Process primary subscribers
        bondData.primarySubscribers.forEach((primarySubscriber) => {
          const requestId = primarySubscriber.id || primarySubscriber.requestId;
          const approval = allApprovalStates[requestId];
          const status = getApprovalStatus(approval);

          // Get request date - try to find from submissions or use approval date
          let requestDate = 'N/A';
          if (primarySubscriber.submissions && primarySubscriber.submissions.length > 0) {
            const firstSubmission = primarySubscriber.submissions[0];
            requestDate = firstSubmission.submittedAt || firstSubmission.uploadedAt || 'N/A';
            if (requestDate !== 'N/A') {
              requestDate = formatDateValue(requestDate);
            }
          } else if (approval?.l1ApprovedAt) {
            requestDate = formatDateValue(approval.l1ApprovedAt);
          } else if (approval?.rejectedAt) {
            requestDate = formatDateValue(approval.rejectedAt);
          }

          rows.push([
            bond.bondName || bond.name || 'N/A',
            bond.isin || '-',
            bond.stockCode || '-',
            bond.productType || bond.bondType || 'N/A',
            bond.issuer || 'N/A',
            primarySubscriber.name || 'N/A',
            primarySubscriber.email || 'N/A',
            (primarySubscriber.totalRequestedAmount || primarySubscriber.totalAmount || 0).toString(),
            status,
            approval?.l1ApprovedBy ? getApproverEmail(approval.l1ApprovedBy) : 'N/A',
            approval?.l1ApprovedAt ? formatDateValue(approval.l1ApprovedAt) : 'N/A',
            approval?.l2ApprovedBy ? getApproverEmail(approval.l2ApprovedBy) : 'N/A',
            approval?.l2ApprovedAt ? formatDateValue(approval.l2ApprovedAt) : 'N/A',
            approval?.rejectedBy ? getApproverEmail(approval.rejectedBy) : 'N/A',
            approval?.rejectedAt ? formatDateValue(approval.rejectedAt) : 'N/A',
            requestDate
          ]);
        });
      });

      // Convert to CSV format
      const csvContent = rows
        .map(row => row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `sub_agreement_management_report_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotice('success', 'Export Successful', `Report exported successfully with ${rows.length - 1} records.`);
    } catch (error) {
      console.error('Error exporting report:', error);
      showNotice('error', 'Export Failed', error.response?.data?.message || error.message || 'Failed to generate report. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sub Agreement Management</h1>
            <p className="mt-2 text-sm text-gray-600">View and manage Sub Agreement requests submitted by Primary Subscribers</p>
          </div>
          <button
            onClick={handleExportReport}
            className="btn-primary"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Status Filter */}
        {!loading && !error && (subAgreementBonds.length > 0 || allBondsForFilter.length > 0) && (
          <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-4">
              <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
                Filter by Status:
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loadingAllBonds}
                className="px-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
              </select>
              {loadingAllBonds && (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Loading all bonds...
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center bg-white rounded-lg shadow">
            <Loader className="w-8 h-8 mx-auto mb-4 text-gray-400 animate-spin" />
            <p className="text-gray-600">Loading bonds with Sub Agreement requests...</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center bg-white rounded-lg shadow">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Bonds</h3>
            <p className="mb-4 text-gray-600">{error}</p>
            <button onClick={fetchSubAgreementBonds} className="btn-primary">
              Retry
            </button>
          </div>
        ) : (subAgreementBonds.length === 0 && allBondsForFilter.length === 0) ? (
          <div className="py-12 text-center bg-white rounded-lg shadow">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">No Bonds with Sub Agreement Requests</h3>
            <p className="max-w-md mx-auto mb-6 text-gray-600">
              Bonds will appear here automatically after Primary Subscribers submitted their Sub Agreement requests for bonds
            </p>
          </div>
        ) : (() => {
          // Use all bonds when filter is applied, otherwise use paginated bonds
          const bondsToFilter = statusFilter !== 'all' && allBondsForFilter.length > 0
            ? allBondsForFilter
            : subAgreementBonds;

          // Show loading state when fetching all bonds for filter
          if (statusFilter !== 'all' && loadingAllBonds) {
            return (
              <div className="py-12 text-center bg-white rounded-lg shadow">
                <Loader className="w-8 h-8 mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-gray-600">Loading all bonds for filtering...</p>
              </div>
            );
          }

          // Filter bonds based on status filter
          const filteredBonds = bondsToFilter.filter((bond) => {
            // If filter is 'all', show all bonds
            if (statusFilter === 'all') return true;

            // Get bond data
            const bondData = subAgreementData[bond.id];
            if (!bondData || !bondData.primarySubscribers || bondData.primarySubscribers.length === 0) {
              return false; // Don't show bonds with no data
            }

            // Check if at least one primary subscriber matches the filter
            return bondData.primarySubscribers.some((ps) => {
              const status = getPrimarySubscriberStatus(ps.id || ps.requestId);
              // When filter is "Pending", match both "Pending" and "L1 Approved"
              if (statusFilter === 'Pending') {
                return status === 'Pending' || status === 'L1 Approved';
              }
              // When filter is "Approved", only match "Approved"
              if (statusFilter === 'Approved') {
                return status === 'Approved';
              }
              return status === statusFilter;
            });
          });

          // Show message if no bonds match the filter
          if (filteredBonds.length === 0 && statusFilter !== 'all') {
            return (
              <div className="py-12 text-center bg-white rounded-lg shadow">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No Bonds Found</h3>
                <p className="mb-4 text-gray-600">
                  No bonds with Sub Agreement requests matching status "{statusFilter}".
                </p>
                <button
                  onClick={() => setStatusFilter('all')}
                  className="btn-primary"
                >
                  Show All Bonds
                </button>
              </div>
            );
          }

          return (
            <div className="space-y-6">
              {filteredBonds.map((bond) => {
                // Get Sub Agreement data for this bond
                const bondSummary = bondSubAgreementSummary[bond.id];
                const bondData = subAgreementData[bond.id];

                // Filter primary subscribers based on status filter
                const filteredPrimarySubscribers = bondData && bondData.primarySubscribers
                  ? bondData.primarySubscribers.filter((ps) => {
                    if (statusFilter === 'all') return true;
                    const status = getPrimarySubscriberStatus(ps.id || ps.requestId);
                    // When filter is "Pending", match both "Pending" and "L1 Approved"
                    if (statusFilter === 'Pending') {
                      return status === 'Pending' || status === 'L1 Approved';
                    }
                    // When filter is "Approved", only match "Approved"
                    if (statusFilter === 'Approved') {
                      return status === 'Approved';
                    }
                    return status === statusFilter;
                  })
                  : [];

                const totalSubAgreementAmount = filteredPrimarySubscribers.length > 0
                  ? filteredPrimarySubscribers.reduce((sum, ps) => sum + (ps.totalRequestedAmount || ps.totalAmount || 0), 0)
                  : (bondSummary
                    ? bondSummary.primarySubscribers.reduce((sum, ps) => sum + (ps.totalRequestedAmount || ps.totalAmount || 0), 0)
                    : null);

                const isLoading = subAgreementLoading[bond.id];

                return (
                  <div key={bond.id} className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-md">
                    {/* Bond Header */}
                    <div className="px-6 py-4 bg-white border-b border-gray-200">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* Bond Details */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{bond.bondName}</h3>
                          <div className="flex flex-wrap items-center mt-1 text-sm text-gray-600 gap-x-4 gap-y-1">
                            <span>{bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}</span>
                            <span className="text-gray-400">•</span>
                            <span>{bond.issuer || 'Unknown Issuer'}</span>
                            {bond.isin && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="font-mono text-blue-600">ISIN: {bond.isin}</span>
                              </>
                            )}
                            {bond.stockCode && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="font-mono text-blue-600">Stock Code: {bond.stockCode}</span>
                              </>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center mt-2 gap-x-4 gap-y-2">
                            <div>
                              <span className="text-xs text-gray-500">Total Amount: </span>
                              <span className="text-sm font-semibold text-gray-900">
                                {typeof totalSubAgreementAmount === 'number'
                                  ? formatCurrency(totalSubAgreementAmount)
                                  : '—'}
                              </span>
                            </div>
                            {bond.subscriptionStartDate && bond.subscriptionEndDate && (
                              <>
                                <div>
                                  <span className="text-xs text-gray-500">Subscription Period: </span>
                                  <span className="text-sm text-gray-700">
                                    {formatDate(bond.subscriptionStartDate)} - {formatDate(bond.subscriptionEndDate)}
                                  </span>
                                </div>
                                <div>
                                  {(() => {
                                    const now = new Date();
                                    const endDate = new Date(bond.subscriptionEndDate);
                                    const startDate = new Date(bond.subscriptionStartDate);

                                    if (now < startDate) {
                                      return (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          Upcoming
                                        </span>
                                      );
                                    } else if (now >= startDate && now <= endDate) {
                                      return (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Active
                                        </span>
                                      );
                                    } else {
                                      return (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                          Completed
                                        </span>
                                      );
                                    }
                                  })()}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {(() => {
                            const subAgreementData = bondSubAgreementSummary[bond.id];
                            if (!subAgreementData) {
                              return (
                                <span className="inline-flex items-center text-sm text-gray-500">
                                  <Loader className="w-4 h-4 mr-2 text-blue-600 animate-spin" />
                                  Loading data...
                                </span>
                              );
                            }

                            // Use filtered primary subscribers for actions if filter is applied
                            const primarySubscribersForActions = statusFilter === 'all'
                              ? (subAgreementData.primarySubscribers || [])
                              : (subAgreementData.primarySubscribers || []).filter((ps) => {
                                const status = getPrimarySubscriberStatus(ps.id || ps.requestId);
                                // When filter is "Pending", match both "Pending" and "L1 Approved"
                                if (statusFilter === 'Pending') {
                                  return status === 'Pending' || status === 'L1 Approved';
                                }
                                // When filter is "Approved", only match "Approved"
                                if (statusFilter === 'Approved') {
                                  return status === 'Approved';
                                }
                                return status === statusFilter;
                              });

                            // Get all request IDs from filtered list
                            const allRequestIds = primarySubscribersForActions.map(ps => ps.id).filter(Boolean);

                            if (allRequestIds.length === 0) {
                              return (
                                <span className="text-xs text-gray-500">No Sub Agreement requests</span>
                              );
                            }

                            // Check approval statuses
                            const allApprovals = allRequestIds.map(id => subAgreementApprovalStates[id]).filter(Boolean);
                            const allL1Approved = allApprovals.length === allRequestIds.length && allApprovals.every(a => a.l1ApprovedBy);
                            const allL2Approved = allApprovals.length === allRequestIds.length && allApprovals.every(a => a.l2ApprovedBy);
                            // Use status field as primary source of truth for rejection
                            const allRejected = allApprovals.length > 0 && allApprovals.every(a => a.status === 'rejected');
                            const currentUserId = authUser?._id || authUser?.id;
                            const allL1BySameUser = allApprovals.length > 0 && allApprovals.every(a =>
                              a.l1ApprovedBy && currentUserId && String(a.l1ApprovedBy) === String(currentUserId)
                            );
                            const needsL1 = !allL1Approved && !allRejected;
                            const needsL2 = allL1Approved && !allL2Approved && !allRejected && !allL1BySameUser;
                            const blockedL2 = allL1Approved && !allL2Approved && !allRejected && allL1BySameUser;

                            const totalEntities = primarySubscribersForActions?.length || 0;

                            // Count approved entities from filtered list
                            const l1ApprovedCount = primarySubscribersForActions.filter(ps => {
                              const approval = subAgreementApprovalStates[ps.id];
                              return approval && approval.l1ApprovedBy;
                            }).length;

                            const l2ApprovedCount = primarySubscribersForActions.filter(ps => {
                              const approval = subAgreementApprovalStates[ps.id];
                              return approval && approval.l2ApprovedBy;
                            }).length;

                            return (
                              <>
                                {needsL1 && (
                                  <button
                                    onClick={() => handleBondApproval(bond, 'L1')}

                                    className="btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={`${l1ApprovedCount} of ${totalEntities} entities approved at L1`}
                                  >
                                    {approvingBonds.has(bond.id) ? (
                                      <Loader className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Approve L1 ({l1ApprovedCount}/{totalEntities})
                                      </>
                                    )}
                                  </button>
                                )}

                                {needsL2 && (
                                  <button
                                    onClick={() => handleBondApproval(bond, 'L2')}

                                    className="bg-green-600 btn-primary btn-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={`${l2ApprovedCount} of ${totalEntities} entities approved at L2`}
                                  >
                                    {approvingBonds.has(bond.id) ? (
                                      <Loader className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Approve L2 ({l2ApprovedCount}/{totalEntities})
                                      </>
                                    )}
                                  </button>
                                )}

                                {/* Bulk Reject Button - show only if there are 2+ pending requests (not fully approved) */}
                                {(() => {
                                  const pendingCount = allRequestIds.filter(requestId => {
                                    const approval = subAgreementApprovalStates[requestId];
                                    const isL2Approved = approval && approval.l2ApprovedBy;
                                    return !isL2Approved;
                                  }).length;

                                  return pendingCount > 1 && !allL2Approved && !allRejected && (
                                    <button
                                      onClick={() => handleBondRejection(bond)}

                                      className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded btn-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {rejectingBonds.has(bond.id) ? (
                                        <Loader className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>Reject All</>
                                      )}
                                    </button>
                                  );
                                })()}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Sub Agreement Details */}
                    <div className="px-6 py-4">
                      {isLoading || !bondData ? (
                        <div className="py-8 text-center">
                          <Loader className="w-6 h-6 mx-auto mb-2 text-blue-600 animate-spin" />
                          <p className="text-sm text-gray-600">Loading Sub Agreement requests...</p>
                        </div>
                      ) : bondData ? (
                        <div className="space-y-4">
                          {/* Primary Subscribers Section */}
                          {filteredPrimarySubscribers && filteredPrimarySubscribers.length > 0 ? (
                            <div>
                              <h4 className="mb-3 text-sm font-semibold text-gray-900">
                                Primary Subscribers ({filteredPrimarySubscribers.length}
                                {statusFilter !== 'all' && bondData.primarySubscribers && bondData.primarySubscribers.length > filteredPrimarySubscribers.length
                                  ? ` of ${bondData.primarySubscribers.length}`
                                  : ''})
                              </h4>
                              <div className="overflow-x-auto bg-white border border-gray-300 rounded-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase">Name</th>
                                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase">Email</th>
                                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase">Amount</th>
                                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase">Status</th>
                                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-700 uppercase">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {filteredPrimarySubscribers.map((primarySubscriber) => {
                                      const requestId = primarySubscriber.id;
                                      const isEditing = editingRequestId === requestId;
                                      const approval = subAgreementApprovalStates[requestId];
                                      const isApproved = approval && (
                                        approval.status === 'completed' ||
                                        approval.status === 'l2_approved' ||
                                        (approval.l1ApprovedBy && approval.l2ApprovedBy)
                                      );
                                      const isL1Approved = approval && approval.l1ApprovedBy && !approval.l2ApprovedBy;
                                      const isPending = !approval || (!approval.l1ApprovedBy && !approval.l2ApprovedBy);

                                      return (
                                        <tr key={primarySubscriber.id} className="bg-white hover:bg-gray-50">
                                          <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                            {primarySubscriber.name || primarySubscriber.email?.split('@')[0] || '—'}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-700">{primarySubscriber.email || '—'}</td>
                                          <td className="px-4 py-3 text-sm">
                                            {isEditing ? (
                                              <div className="space-y-1">
                                                <input
                                                  type="number"
                                                  value={editingAmount}
                                                  onChange={(e) => setEditingAmount(e.target.value)}
                                                  className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                                                  disabled={submissionSaving}
                                                />
                                                {approval && (approval.originalRequestedAmount || approval.totalRequestedAmount) && (
                                                  <div className="text-xs text-gray-500">
                                                    Max: {formatCurrency(approval.originalRequestedAmount || approval.totalRequestedAmount)}
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <span className="font-medium text-gray-700">
                                                {formatCurrency(primarySubscriber.totalRequestedAmount || primarySubscriber.totalAmount)}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-4 py-3 text-sm">
                                            {(() => {
                                              // Use status as primary source of truth
                                              const isRejected = approval && approval.status === 'rejected';
                                              if (isRejected) {
                                                return (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Rejected
                                                  </span>
                                                );
                                              }
                                              if (isApproved) {
                                                return (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Approved
                                                  </span>
                                                );
                                              }
                                              if (isL1Approved) {
                                                return (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    L1 Approved
                                                  </span>
                                                );
                                              }
                                              return (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                  <Clock className="w-3 h-3 mr-1" />
                                                  Pending
                                                </span>
                                              );
                                            })()}
                                          </td>
                                          <td className="px-2 py-3 text-sm">
                                            <div className="flex items-center gap-1">
                                              {isEditing ? (
                                                <>
                                                  <button
                                                    onClick={handleSaveSubmissionAmount}
                                                    disabled={submissionSaving}
                                                    className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                                  >
                                                    {submissionSaving ? 'Saving...' : 'Save'}
                                                  </button>
                                                  <button
                                                    onClick={handleCancelEditSubmission}
                                                    disabled={submissionSaving}
                                                    className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                                                  >
                                                    Cancel
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  <button
                                                    onClick={() => handleViewDetails(primarySubscriber)}

                                                    className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="View details"
                                                  >
                                                    Details
                                                  </button>
                                                  {!isApproved && (
                                                    <button
                                                      onClick={() => handleStartEditSubmission(primarySubscriber)}

                                                      className="px-2 py-1 text-xs font-medium text-blue-600 rounded bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                      Edit
                                                    </button>
                                                  )}
                                                  {(() => {
                                                    // Use status as primary source of truth
                                                    const isRejected = approval && approval.status === 'rejected';
                                                    const canL1 = isPending && !isRejected;
                                                    const l1ApproverId = approval?.l1ApprovedBy;
                                                    const currentUserId = authUser?._id || authUser?.id;
                                                    const sameUserL1 = !!(isL1Approved && currentUserId && l1ApproverId &&
                                                      String(l1ApproverId) === String(currentUserId)
                                                    );
                                                    const canL2 = isL1Approved && !sameUserL1 && !isRejected;
                                                    const canReject = !isApproved && !isRejected;

                                                    return (
                                                      <>
                                                        {canL1 && (
                                                          <button
                                                            onClick={() => handleApproveSubAgreement(requestId, 'L1')}

                                                            className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                          >
                                                            {approvingRequests.has(requestId) ? 'Approving...' : 'Approve L1'}
                                                          </button>
                                                        )}
                                                        {canL2 && (
                                                          <button
                                                            onClick={() => handleApproveSubAgreement(requestId, 'L2')}

                                                            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                          >
                                                            {approvingRequests.has(requestId) ? 'Approving...' : 'Approve L2'}
                                                          </button>
                                                        )}
                                                        {canReject && (
                                                          <button
                                                            onClick={() => handleRejectSubAgreement(requestId)}

                                                            className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                          >
                                                            {rejectingRequests.has(requestId) ? 'Rejecting...' : 'Reject'}
                                                          </button>
                                                        )}
                                                        {isApproved && (
                                                          <span className="text-xs font-medium text-green-600">
                                                            Fully Approved
                                                          </span>
                                                        )}
                                                        {isRejected && (
                                                          <span className="text-xs font-medium text-red-600">
                                                            Rejected
                                                          </span>
                                                        )}
                                                        {isL1Approved && sameUserL1 && (
                                                          <span className="text-xs font-medium text-yellow-600">
                                                            Awaiting L2
                                                          </span>
                                                        )}
                                                      </>
                                                    );
                                                  })()}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-3 text-right">
                                <span className="text-sm font-semibold text-gray-700">
                                  Total: {formatCurrency(
                                    filteredPrimarySubscribers.reduce((sum, ps) => sum + (ps.totalRequestedAmount || ps.totalAmount || 0), 0)
                                  )}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="py-8 text-center">
                              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                              <h3 className="mb-1 text-sm font-medium text-gray-900">No Primary Subscribers</h3>
                              <p className="text-xs text-gray-600">
                                {statusFilter !== 'all'
                                  ? `No primary subscribers with status "${statusFilter}" for this bond.`
                                  : 'No primary subscribers have submitted Sub Agreement requests for this bond yet.'}
                              </p>
                            </div>
                          )}

                          {/* Empty State */}
                          {(!bondData.primarySubscribers || bondData.primarySubscribers.length === 0) && (
                            <div className="py-8 text-center">
                              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                              <h3 className="mb-1 text-sm font-medium text-gray-900">No Sub Agreement Requests</h3>
                              <p className="text-xs text-gray-600">
                                No primary subscribers have submitted Sub Agreement requests for this bond yet.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="py-8 text-center">
                          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                          <p className="text-sm text-gray-600">Failed to load Sub Agreement data</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Pagination Controls */}
        {!loading && !error && totalRecords > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 mt-4 bg-white border-t border-gray-200 rounded-lg shadow sm:px-6">
            <div className="flex justify-between flex-1 sm:hidden">
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    fetchSubAgreementBonds(newPage, pageSize);
                  }
                }}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  if (currentPage < totalPages) {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    fetchSubAgreementBonds(newPage, pageSize);
                  }
                }}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * pageSize, totalRecords)}</span> of{' '}
                  <span className="font-medium">{totalRecords}</span> bonds
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="pageSize" className="text-sm text-gray-700">
                  Per page:
                </label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value);
                    setPageSize(newSize);
                    setCurrentPage(1);
                    fetchSubAgreementBonds(1, newSize);
                  }}
                  className="text-sm border-gray-300 rounded-md"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => {
                      if (currentPage > 1) {
                        const newPage = currentPage - 1;
                        setCurrentPage(newPage);
                        fetchSubAgreementBonds(newPage, pageSize);
                      }
                    }}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    &laquo;
                  </button>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                    if (endPage - startPage + 1 < maxVisible) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <button
                          key="1"
                          onClick={() => {
                            setCurrentPage(1);
                            fetchSubAgreementBonds(1, pageSize);
                          }}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => {
                            setCurrentPage(i);
                            fetchSubAgreementBonds(i, pageSize);
                          }}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${i === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => {
                            setCurrentPage(totalPages);
                            fetchSubAgreementBonds(totalPages, pageSize);
                          }}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                  <button
                    onClick={() => {
                      if (currentPage < totalPages) {
                        const newPage = currentPage + 1;
                        setCurrentPage(newPage);
                        fetchSubAgreementBonds(newPage, pageSize);
                      }
                    }}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    &raquo;
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {detailsModal.open && detailsModal.request && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Sub Agreement Details</h3>
                <button
                  onClick={handleCloseDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-6">
                {/* Primary Subscriber Information */}
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-gray-900">Primary Subscriber Information</h4>
                  <div className="p-4 space-y-2 rounded-lg bg-gray-50">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="text-sm font-medium text-gray-900">{detailsModal.request.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm font-medium text-gray-900">{detailsModal.request.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Requested Amount</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(detailsModal.request.totalRequestedAmount || detailsModal.request.totalAmount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <div className="mt-1">
                          {(() => {
                            const requestId = detailsModal.request.id;
                            const approval = subAgreementApprovalStates[requestId];
                            const isRejected = approval && approval.status === 'rejected';
                            const isApproved = approval && (
                              approval.status === 'completed' ||
                              approval.status === 'l2_approved' ||
                              (approval.l1ApprovedBy && approval.l2ApprovedBy)
                            );
                            const isL1Approved = approval && approval.l1ApprovedBy && !approval.l2ApprovedBy;

                            if (isRejected) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Rejected
                                </span>
                              );
                            }
                            if (isApproved) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </span>
                              );
                            }
                            if (isL1Approved) {
                              return (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  L1 Approved
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submissions */}
                {detailsModal.request.submissions && detailsModal.request.submissions.length > 0 && (
                  <div>
                    <h4 className="mb-3 text-sm font-semibold text-gray-900">Submitted Documents ({detailsModal.request.submissions.length})</h4>
                    <div className="space-y-3">
                      {detailsModal.request.submissions.map((submission, index) => (
                        <div key={submission._id || submission.id || index} className="p-4 rounded-lg bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2 space-x-2">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <p className="text-sm font-medium text-gray-900">
                                  {submission.fileName || `Submission ${index + 1}`}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                  <p className="text-gray-500">Uploaded At</p>
                                  <p className="text-gray-900">
                                    {submission.uploadedAt ? formatDate(submission.uploadedAt) : '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">File Size</p>
                                  <p className="text-gray-900">
                                    {submission.fileSize ? `${(submission.fileSize / 1024).toFixed(2)} KB` : '—'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center ml-4 space-x-2">
                              {(submission.fileUrl || submission.url) && (
                                <>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const fileUrl = submission.fileUrl || submission.url;
                                        const submissionId = submission._id || submission.id;

                                        // Check if we already have a blob URL for this submission
                                        if (pdfBlobUrls[submissionId]) {
                                          setSelectedPdfUrl(pdfBlobUrls[submissionId]);
                                          return;
                                        }

                                        // Fetch the file with authentication
                                        const response = await fetch(fileUrl, {
                                          method: 'GET',
                                          credentials: 'include',
                                          headers: {
                                            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                                          },
                                        });

                                        if (!response.ok) {
                                          throw new Error(`Failed to fetch file: ${response.statusText}`);
                                        }

                                        const blob = await response.blob();
                                        const blobUrl = window.URL.createObjectURL(blob);

                                        // Store the blob URL
                                        setPdfBlobUrls(prev => ({ ...prev, [submissionId]: blobUrl }));
                                        setSelectedPdfUrl(blobUrl);
                                      } catch (error) {
                                        showNotice('error', 'Preview Failed', 'Failed to load PDF preview');
                                      }
                                    }}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                    title="Preview PDF"
                                  >
                                    Preview
                                  </button>
                                  <button
                                    onClick={() => handleDownloadPdf(submission)}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                    title="Download PDF"
                                  >
                                    Download
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PDF Preview */}
                {selectedPdfUrl && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">PDF Preview</h4>
                      <button
                        onClick={() => setSelectedPdfUrl(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Close Preview
                      </button>
                    </div>
                    <div className="overflow-hidden bg-gray-100 border border-gray-300 rounded-lg" style={{ height: '500px' }}>
                      <iframe
                        src={selectedPdfUrl}
                        className="w-full h-full"
                        title="PDF Preview"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200">
                <button
                  onClick={handleCloseDetailsModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{confirmModal.title}</h3>
              </div>
              <div className="px-6 py-4">
                <p className="mb-4 text-sm text-gray-700">{confirmModal.message}</p>
                {confirmModal.showInput && (
                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      {confirmModal.inputLabel}
                    </label>
                    {confirmModal.isTextArea ? (
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder={confirmModal.inputPlaceholder}
                        value={confirmModal.inputValue}
                        onChange={(e) => setConfirmModal(prev => ({ ...prev, inputValue: e.target.value }))}
                      />
                    ) : (
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={confirmModal.inputPlaceholder}
                        value={confirmModal.inputValue}
                        onChange={(e) => setConfirmModal(prev => ({ ...prev, inputValue: e.target.value }))}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end px-6 py-4 space-x-3 border-t border-gray-200">
                <button
                  onClick={() => setConfirmModal({ ...confirmModal, open: false, inputValue: '' })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const callback = confirmModal.onConfirm;
                    const value = confirmModal.inputValue;

                    // For handlers that need validation, keep modal open until callback completes
                    if (callback) {
                      const result = callback(value);
                      // If callback returns a promise, wait for it
                      if (result && typeof result.then === 'function') {
                        await result;
                      }
                    }
                    setConfirmModal({ ...confirmModal, open: false, inputValue: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Toast */}
        {notice.open && (
          <div className="fixed z-50 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg top-4 right-4 w-80 animate-fade-in">
            <div className={`px-4 py-3 flex items-start space-x-3 ${notice.type === 'success' ? 'bg-green-50' : notice.type === 'error' ? 'bg-red-50' : notice.type === 'warning' ? 'bg-yellow-50' : 'bg-blue-50'}`}>
              <div className="mt-0.5">
                {notice.type === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
                {notice.type === 'error' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                {notice.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                {notice.type === 'info' && <FileText className="w-5 h-5 text-blue-500" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold leading-snug text-gray-900">{notice.title}</p>
                <p className="mt-0.5 text-xs text-gray-700 leading-snug whitespace-pre-line">{notice.message}</p>
              </div>
              <button onClick={closeNotice} className="text-gray-400 hover:text-gray-600" aria-label="Close notification">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative w-full h-1 bg-gray-100">
              <div className={`absolute inset-y-0 left-0 ${notice.type === 'success' ? 'bg-green-500' : notice.type === 'error' ? 'bg-red-500' : notice.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'} animate-notice-progress`}></div>
            </div>
          </div>
        )}

        {/* SubAgreemental styles for animations */}
        <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fadeIn 0.25s ease-out; }
        @keyframes noticeProgress { from { width: 100%; } to { width: 0%; } }
        .animate-notice-progress { animation: noticeProgress 5s linear forwards; }
      `}</style>
      </div>
    </div>
  );
};

export default SubAgreementManagement;

